import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { com } from "../proto/game.pb";
import { useNotification, useWebSocket } from "../shared/contexts";
import { MT } from "../shared/types/proto/mt";

export interface AutoPlaySettings {
  numberOfRounds: number | null;
  stopOnWin: boolean;
  stopOnWinAmount: number;
  stopOnLoss: boolean;
  stopOnLossAmount: number;
  stopOnBalance: boolean;
  stopOnBalanceAmount: number;
}

export interface AutoPlayInfo {
  isActive: boolean;
  roundId?: number | null;
  roundsPlayed?: number | null;
  roundsRemaining?: number | null;
  totalRounds?: number | null;
  totalWagered?: number | null;
  netProfit?: number | null;
  slotId?: number | null;
}

type StartParams = {
  slotId?: number;
  betAmount: number;
  rounds?: number;
  stopOnLoss?: number;
  stopOnProfit?: number;
  stopOnSingleWin?: number;
};

export const useAutoPlay = (slotId: number) => {
  const { onMessage, sendProto } = useWebSocket();
  const { showError } = useNotification();
  const [isAutoPlayActive, setIsAutoPlayActive] = useState(false);
  const [autoInfo, setAutoInfo] = useState<AutoPlayInfo | null>(null);
  const isActiveRef = useRef(false);

  const startAutoPlay = useCallback(
    (params: StartParams): boolean => {
      if (!params.rounds) {
        showError("Please, set number of rounds");
        return false;
      }
      if (
        params.stopOnLoss == null &&
        params.stopOnProfit == null &&
        params.stopOnSingleWin == null
      ) {
        showError("Please, specify at least one stop condition");
        return false;
      }

      const payload: com.achlid.crash.game.IGameMessage = {
        type: MT.AUTO_PLAY,
        autoPlay: {
          slotId: params.slotId ?? slotId ?? 0,
          betAmount: params.betAmount,
          rounds: params.rounds ?? 0,
          stopOnLoss: params.stopOnLoss,
          stopOnProfit: params.stopOnProfit,
          stopOnSingleWin: params.stopOnSingleWin,
        },
      };

      return sendProto(payload);
    },
    [sendProto, showError, slotId]
  );

  const stopAutoPlay = useCallback(
    (forcedSlotId?: number): boolean => {
      const payload: com.achlid.crash.game.IGameMessage = {
        type: MT.STOP_AUTO_PLAY,
        stopAutoPlay: {
          slotId: forcedSlotId ?? slotId ?? 0,
        },
      };
      return sendProto(payload);
    },
    [sendProto, slotId]
  );

  useEffect(() => {
    const off = onMessage(MT.AUTO_PLAY_RESULT, (message) => {
      const res = message.autoPlayResult;
      if (!res) return;
      if (res.slotId !== slotId) return;
      const { stopReason } = res;
      const nextActive = !!(res.success && !stopReason);
      if (isActiveRef.current !== nextActive) {
        isActiveRef.current = nextActive;
        setIsAutoPlayActive(nextActive);
      }
      setAutoInfo((prev) => ({
        isActive: nextActive,
        roundId: prev?.roundId ?? null,
        roundsPlayed: prev?.roundsPlayed ?? null,
        roundsRemaining: prev?.roundsRemaining ?? null,
        totalRounds: prev?.totalRounds ?? null,
        totalWagered: prev?.totalWagered ?? null,
        netProfit: prev?.netProfit ?? null,
        slotId,
      }));
    });
    const autoPlayUpdate = onMessage(MT.AUTOPLAY_UPDATE, (message) => {
      const res = message.autoPlayUpdate;
      if (!res) return;
      if (res.slotId !== slotId) return;
      const nextActive = !!res.isActive;
      if (isActiveRef.current !== nextActive) {
        isActiveRef.current = nextActive;
        setIsAutoPlayActive(nextActive);
      }
      setAutoInfo({
        isActive: nextActive,
        roundId: res.roundId != null ? Number(res.roundId as any) : null,
        roundsPlayed:
          res.roundsPlayed != null ? Number(res.roundsPlayed as any) : null,
        roundsRemaining:
          res.roundsRemaining != null
            ? Number(res.roundsRemaining as any)
            : null,
        totalRounds:
          res.totalRounds != null ? Number(res.totalRounds as any) : null,
        totalWagered:
          res.totalWagered != null ? Number(res.totalWagered as any) : null,
        netProfit: res.netProfit != null ? Number(res.netProfit as any) : null,
        slotId: res.slotId != null ? Number(res.slotId as any) : null,
      });
    });

    return () => {
      off();
      autoPlayUpdate();
    };
  }, [onMessage, slotId]);

  return {
    isAutoPlayActive,
    autoInfo,
    startAutoPlay,
    stopAutoPlay,
  };
};
